// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2024-10-07 17:40:00 +0200 (Mon, 07 Oct 2024) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 146647 $
//  Purpose     : call tree apis
// =============================================================================
// ---Third-party imports
import now from 'performance-now'
import {
  put,
  takeEvery,
  take,
  call,
  throttle,
  all,
  fork,
  spawn,
  putResolve,
  actionChannel,
  race,
  join,
} from 'redux-saga/effects'
import {channel, END} from 'redux-saga'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import {
  has,
  get,
  range,
  uniq,
  filterItems,
  dict,
  size,
  groupBy,
  entries,
  mapItems,
  flatten,
  values,
  sum,
  isArray,
  isNil,
  isString,
  items,
} from 'ldash'
import Api from 'uix/api'
import {initializeDomain, select, buffers, delay, matchServerEvent} from 'uix/sagas'
import log from 'uix/logging'
import {normalize, normalizeByDatatype} from 'uix/normalize'
import {isSSR} from 'uix/utils'

// ---Local imports
import {
  //~ fetchDescendants,
  //~ setDescendants,
  fetchTreeList,
  setTreeList,
  setTreeListLoading,
  setTreeListError,
  getTreeRecords,
  setTreeRecords,
  DBLoadActivity,
  setTrackedRecordsFields,
  setTrackedSkips,
  copyIdsToClipboard,
  storeIdsForClipboard,
  progressTreeRecords,
} from './actions'

// ---Globals

const BATCH_SIZE_FIELDS = 10000
const BATCH_PARALLEL = 2
// if fields from slow namespaces are detected, reduce batchsize
const SLOW_NS = {restriction: 2000, desccount: 100}
// timeout (in ms) for api/treeFields
const TIMEOUT_FIELDS = 30000

export function* listTree({
  /* access api/treeList (fastTreeList)*/
  parentid,
  odid,
  dname,
  typeName,
  otids, // direct list of otids, ignore parentid
  stateNames,
  rootFolder,
  descendants,
  includeParent,
  subdomains,
  links,
  deleted,
  parentType,
  limit,
  customListName,
  customListParams = {},
  query, // only for customListName mode
  ...invalid
}) {
  if (size(invalid) > 0) {
    log.warn('invalid params: %o', invalid)
  }

  if (isArray(typeName)) {
    typeName = typeName.join(',')
  }

  let params = {typeName, stateNames, rootFolder, links, deleted}
  if (otids) {
    if (size(otids) == 0) {
      log.warn('got empty otid list')
    }
    params = {...params, otids}
  } else {
    params = {...params, parentid, parentType}
  }

  // full params override
  let funcname = 'treeList'
  if (customListName) {
    // enter customListName mode
    let name = customListName
    params = {name, typeName, stateNames, ...customListParams}
    if (!otids) {
      // XXX: has otids any meaning here?
      params = {...params, parentid, parentType}
    }
    if (!isNil(query)) {
      params = {...params, query: JSON.stringify(query)}
    }
    funcname = 'customTreeList'
  }

  // also for customTreeList / customListName mode
  if (!isNil(limit)) {
    params.limit = limit
  }
  if (!isNil(subdomains)) {
    params.subdomains = subdomains
  }
  if (!isNil(descendants)) {
    params.descendants = descendants
  }
  if (!isNil(includeParent)) {
    params.includeParent = includeParent
  }

  try {
    params = dict(filterItems(params, (k, v) => v !== undefined && v !== null && v !== ''))
    let result = yield Api.post(`/api/${funcname}`, params, {timeout: Infinity}, {odid, dname})
    if (result.data && result.data.rows === undefined) {
      log.error('no rows in list result: %o', result)
      return {error: {code: 'SERVER_ERROR'}}
    }
    if (result.error) {
      return {error: {...result.error, code: 'SERVER_ERROR'}}
    }
    let data = normalize(get(result, 'data.rows', []), result.data.meta)
    return {data, meta: result.data.meta}
  } catch (e) {
    log.exception(e)
    return {error: {code: 'FETCH_ERROR'}}
  }
}

export function* initializeLinkDomains(data) {
  /* check for missing initialized domains on links due to fat linked tree records
   */
  let linkedNodes = data.filter(e => e.odlink)
  if (linkedNodes.length > 0) {
    let root = yield select()
    let odid2name = root.config.domains.odid2name
    let loadedDomains = root.config.ddata
    let linkedDomainNames = uniq(linkedNodes.map(e => odid2name[e.odlink]))
    let missing = linkedDomainNames.filter(n => !loadedDomains[n])
    if (missing.length > 0) {
      log.debug('got %o missing domains from %o linked nodes', missing.length, linkedNodes.length)
      // really fully parallel?
      yield all(missing.map(dname => call(initializeDomain, dname)))
    }
  }
}
export function* initializeRecordDomains(data) {
  /* check for missing initialized domains on all records
   */
  let root = yield select()
  let odid2name = root.config.domains.odid2name
  let loadedDomains = root.config.ddata
  let domainNames = uniq(data.map(e => odid2name[e.odid]))
  let missing = domainNames.filter(n => !loadedDomains[n])
  if (missing.length > 0) {
    log.debug('got %o missing domains from %o linked nodes', missing.length, data.length)
    // really fully parallel?
    yield all(missing.map(dname => call(initializeDomain, dname)))
  }
}

function* _fetchDescendants({payload}) {
  let {node} = payload
  let {otid, odid, idContext, typeName} = node
  let {data, error} = yield listTree({parentid: otid, odid, typeName, descendants: true})
  if (error) {
    log.error('list tree failed (descendants): %o', error)
  } else {
    yield initializeLinkDomains(data)
    yield put(setDescendants(node, data))
  }
}

function* _fetchTreeList({payload}) {
  let {listid, params, paramsKey} = payload
  yield put(setTreeListLoading(listid, paramsKey))
  let {data, error} = yield listTree(params)
  if (error) {
    log.error('list tree failed: %o', error)

    yield put(setTreeListError(listid, error))
  } else {
    // XXX: this can become expensive
    //~ yield initializeLinkDomains(data)
    yield put(setTreeList(listid, data, paramsKey))
  }
}

function* trackSkippedNamespacesForDatatype(byDatatype) {
  /* we understand which columns/fields are not appropriate for a datatype
  without loading the full schema
  */
  let {trackedSkips} = yield select(root => root.data.db)

  let nextTrackedSkips = mapItems(byDatatype, (dt, {meta}) => [dt, meta.datatype_skip]).filter(
    ([dt, skips]) => [dt, uniq([...(trackedSkips[dt] || []), ...skips])]
  )
  let changed =
    nextTrackedSkips.filter(([dt, skips]) => size(skips) > size(trackedSkips[dt])).length > 0
  if (changed) {
    yield put(setTrackedSkips({...trackedSkips, ...dict(nextTrackedSkips)}))
  }
}

function* trackFetchedFieldsForIds(nodes, fields) {
  /* track loaded columns/details for after fetching new /api/treeFields data
  XXX: we store a list of strings for each otid - maybe this is expensive consumption
   */
  let {trackedFields} = yield select(root => root.data.db)
  let selected_fields = new Set(fields)
  const different = (a, b) => [...a].filter(x => !b.has(x)).length > 0

  let nextTrackedFields = dict(
    nodes.map(node => {
      let {otid} = node
      let nextFields = selected_fields
      let {fields: current} = trackedFields[otid] ?? {}
      if (current) {
        if (!fields.every(f => current.has(f))) {
          // XXX: maybe share the union over more rows
          nextFields = new Set([...current, ...fields])
        }
      }
      // also store last node data for refetch
      return [otid, {fields: nextFields, node}]
    })
  )
  yield put(setTrackedRecordsFields(nextTrackedFields))
}

function* filterTrackedFields(byDatatype, fields) {
  /* filter byDatatype down before fetching new /api/treeFields data
   */
  let {trackedFields} = yield select(root => root.data.db)
  let filteredFields = new Set()
  let full_set = false
  let has_missing = otid_fields => {
    if (!otid_fields) {
      if (!full_set) {
        fields.forEach(f => filteredFields.add(f))
        full_set = true
      }
      return true
    }

    let missing = fields.filter(f => !otid_fields.has(f))
    missing.forEach(f => filteredFields.add(f))
    return missing.length > 0
  }

  let filteredByDatatype = dict(
    mapItems(byDatatype, (dt, otids) => [
      dt,
      otids.filter(otid => has_missing(trackedFields[otid]?.fields)),
    ]).filter(e => size(e[1]) > 0)
  )

  return {filteredFields: [...filteredFields], filteredByDatatype}
}

function* _fetchFieldsBatch(nodes, batch, fields, links) {
  /* use treeFields api
  nodes: flat list of nodes
  batch: nodes by datatype
   */
  yield trackFetchedFieldsForIds(nodes, fields)

  let {data, error} = yield Api.post(
    `/api/treeFields`,
    {
      byDatatype: JSON.stringify(batch),
      select: fields,
      format: 'compressed',
    },
    {timeout: TIMEOUT_FIELDS}
  )
  if (error) {
    log.error('error during fetching fields %o', error)
    // XXX: we cant be that noisy for fetching broken fields during wiki-editing
    //~ yield put(Actions.errorNotification({error, header: 'Fetch-Fields-Error'}))
    return []
  } else if (data?.byDatatype) {
    let normalized = normalizeByDatatype(data.byDatatype, data.meta)
    yield trackSkippedNamespacesForDatatype(data.byDatatype)
    return normalized
  }
}

function* _forkBatches(num_workers, fields, links, batches, total_count, batchId) {
  /* distribute over workers and collect results
   */
  const input_chan = yield call(channel)
  const result_chan = yield call(channel)

  // store worker resuls
  let all_normalized = []

  function* _work(i) {
    while (true) {
      let normalized = yield _fetchFieldsBatch(...(yield take(input_chan)))
      all_normalized.push(normalized)
    }
  }

  let func_args = batches.map(b => [b[0], b[1], fields, links])
  for (let args of func_args) {
    yield put(input_chan, args)
  }
  for (let i of range(num_workers)) {
    yield fork(_work, i)
    // an END marker (stops take(input_chan) in _work) for each worker
    yield put(input_chan, END)
  }
  // number of batches with fetch result
  let step = all_normalized.length
  let steps = func_args.length
  let progress = (step, retrieved) =>
    progressTreeRecords({batchId, step, steps, retrieved, total: total_count})
  // reset at the beginning
  yield putResolve(progress(0, 0))
  while (step < func_args.length) {
    if (all_normalized.length != step) {
      // update
      step = all_normalized.length
      if (batchId) {
        let retrieved = sum(all_normalized.map(n => size(n)))
        yield putResolve(progress(step, retrieved))
      }
    }

    yield delay(10)
  }
  let normalized = flatten(all_normalized)

  // update all result at once (TreeTable will only recalc once)
  yield putResolve(setTreeRecords(normalized, links, fields))
  // reset/hide progressbar after some delay
  yield delay(1000)
  yield putResolve(progress(0, 0))
}

function* _treeFieldsBatched(byDatatype, fields, links, batchsize, batchId) {
  let ts = now()
  let flat_nodes = flatten(mapItems(byDatatype, (dt, otids) => otids.map(otid => [dt, otid])))
  let nodes = flat_nodes
  let batches = []
  while (nodes.length > 0) {
    let batch_nodes = nodes.slice(0, batchsize).map(e => ({datatype: e[0], otid: e[1]}))
    let batch_bydt = dict(
      mapItems(groupBy(batch_nodes, e => e.datatype), (k, ns) => [k, ns.map(n => n.otid)])
    )
    batches.push([batch_nodes, batch_bydt])
    nodes = nodes.slice(batchsize)
  }

  yield _forkBatches(BATCH_PARALLEL, fields, links, batches, flat_nodes.length, batchId)
  log.debug(`fetched fields for ${flat_nodes.length} nodes in ${parseInt((now() - ts) / 1000)}s`)
}

function* _getTreeRecords({payload}) {
  /* fetch detail fields/columns for nodes given as [{otid, datatype}, ...]
   */
  let {nodes, fields, force = false, batchId} = payload
  if (size(fields) == 0) {
    log.warn('no fields given')
    return
  }
  // api/treeFields supports only physical nodes
  // so collapse list into physicals and store links for
  // expansion (in setTreeRecords reducer)
  let physicals = []
  let links = []
  if (nodes) {
    let resolved = {}
    for (let e of nodes) {
      if (e.otlink) {
        if (!resolved[e.otlink]) {
          physicals.push({...e, otid: e.otlink, odid: e.odlink, otlink: null})
          resolved[e.otlink] = true
        }
        links.push(e)
      } else {
        physicals.push(e)
        resolved[e.otid] = true
      }
    }
  }
  nodes = physicals

  let byDatatype = groupBy(nodes, e => e.datatype)

  byDatatype = dict(mapItems(byDatatype, (k, v) => [k, v.map(e => e.otid)]))

  // reuse already stored data fields
  if (!force) {
    var {filteredByDatatype, filteredFields} = yield call(filterTrackedFields, byDatatype, fields)
    if (!size(filteredByDatatype) || !size(filteredFields)) {
      log.debug('reusing fields for %o tree nodes', size(nodes))
      return
    } else {
      log.debug(
        'fetching fields for %o [%o]',
        sum(values(filteredByDatatype).map(otids => otids.length)),
        filteredFields.length < 5 ? filteredFields.join(', ') : `${filteredFields.length} fields`
      )
    }
  } else {
    var [filteredByDatatype, filteredFields] = [byDatatype, fields]
  }

  let count = sum(values(filteredByDatatype).map(otids => otids.length))
  // seen a "is not a func" in the f.startsWith check
  filteredFields = filteredFields.filter(f => isString(f))
  let slow_bs = items(SLOW_NS)
    .filter(([ns, bs]) => filteredFields.some(f => f.startsWith(`${ns}.`)))
    .map(([ns, bs]) => bs)
  let batch_size = Math.min(BATCH_SIZE_FIELDS, ...slow_bs)

  // XXX: determine batchsize from duration of first shot
  if (count < batch_size) {
    // no batching
    let normalized = yield _fetchFieldsBatch(nodes, filteredByDatatype, filteredFields, links)
    yield putResolve(setTreeRecords(normalized, links, filteredFields))
  } else {
    yield _treeFieldsBatched(filteredByDatatype, filteredFields, links, batch_size, batchId)
  }
}

export function* trackActivityCall(activity, func, action) {
  yield putResolve(DBLoadActivity({[activity]: true}))
  yield call(func, action)
  yield putResolve(DBLoadActivity({[activity]: false}))
}

const channelWatcher = (actionName, handler) => {
  function* at_once(action) {
    yield call(trackActivityCall, actionName, handler, action)
  }
  function* serialized() {
    let chan = yield actionChannel(actionName, buffers.fixed(10))
    while (true) {
      let action = yield take(chan)
      yield call(trackActivityCall, actionName, handler, action)
    }
  }
  if (isSSR) {
    return takeEvery(actionName, at_once)
  } else {
    return serialized()
  }
}

function* _updateCaption({payload}) {
  let {otid} = payload
  let args = {force: true, nodes: [{otid, datatype: 1}], fields: ['caption']}
  yield _getTreeRecords({payload: args})
}

// XXX: no real throttle - we wont wakeup if we get new data each 500ms
const WATCH_EVENTS = [
  'ERecordDataChanged',
  'ESetReleaseState',
  'EStatusChanged',
  'ECaptionChanged',
  'ERecordInserted',
  'ERecordMoved',
]
const THROTTLE_REFETCH = 1000 // collect ERecordDataChanged for 1 seconds and then refetch
function* watchServerDataChanged() {
  /* watch for SERVER/Events and
  refetch db data via trackedFields
  */
  let invalidated_otids = []
  try {
    while (true) {
      let take_evts = dict(WATCH_EVENTS.map(e => [e, take(matchServerEvent(e))]))
      let {timeout, ...took} = yield race({timeout: delay(THROTTLE_REFETCH), ...take_evts})

      // normalize to empty lists
      // XXX: we receive only one event in took, keep list processing?
      let evts = dict(WATCH_EVENTS.map(e => [e, took[e] ? [took[e].payload] : []]))
      //~ log.x('db-sagas', evts)
      // extract otids
      let event_otids = [
        ...flatten([...evts.ERecordDataChanged, ...evts.ESetReleaseState].map(evt => evt.otids)),
        ...[...evts.EStatusChanged, ...evts.ECaptionChanged].map(evt => evt.otid),
        ...[...evts.ERecordInserted, ...evts.ERecordMoved].map(evt => evt.otparent),
        ...evts.ERecordMoved.map(evt => evt.otparent_old),
      ]

      if (timeout) {
        // after timeout process collected otids
        if (invalidated_otids.length > 0) {
          let otids = uniq(invalidated_otids)
          let {trackedFields} = yield select(root => root.data.db)
          // _getTreeRecords will also update root.data.tree.byId (as a side effect)
          // so we also collect candidates from there
          let treeById = yield select(root => root.data.tree.byId)

          let all_fields = new Set(['odid', 'datatype', 'subtype', 'caption'])
          let nodes = otids
            .filter(otid => trackedFields[otid] || treeById[otid])
            .map(otid => {
              if (trackedFields[otid]) {
                let {fields, node} = trackedFields[otid]
                fields.forEach(all_fields.add, all_fields)
                return node
              } else {
                // no additional fields but a simple node refresh (use-case was caption)
                return treeById[otid]
              }
            })

          let fields = [...all_fields]
          if (nodes.length > 0 && fields.length > 0) {
            log.debug('refetch recs %o', nodes)
            yield _getTreeRecords({payload: {force: true, nodes, fields}})
          }

          invalidated_otids = []
        }
      } else if (event_otids.length > 0) {
        invalidated_otids.push(...event_otids)
      }
    }
  } catch (e) {
    log.exception(e)
  }
}

export const sagas = {
  watchFetchTreeList: channelWatcher('FETCH_TREE_LIST', _fetchTreeList),
  watchGetTreeRecords: channelWatcher('GET_TREE_RECORDS', _getTreeRecords),

  // this will update data.db.byId and also data.tree.byId
  updateCaption: takeEvery('SAVE_MODS_SUCCESS', _updateCaption),

  copyIdsToClipboard: takeEvery(copyIdsToClipboard, function*({payload}) {
    let request = yield select(Sel.request)
    if (navigator) {
      navigator.clipboard.writeText(payload.otids.join(', '))
    }
    yield put(storeIdsForClipboard({otids: payload.otids, request}))
  }),

  subscribeRecordChangedEvents: takeEvery('SET_USER', function*({payload}) {
    // not in SSR
    if (isSSR) return

    let {user} = payload
    if (user.puid) {
      yield put(Actions.subscribeServerEvent(WATCH_EVENTS, true))
    }
  }),
  watchServerDataChanged: watchServerDataChanged(),
}
